/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description utils for claim work lists
 */
import _ from 'lodash';
import { JsonGroup, JsonTree } from '@react-awesome-query-builder/antd';

import dateParser from 'lib/dateParser';
import { defaultTableSelectionMeta } from 'lib/agGrid/AgGridTable';
import { mapClaimStatusCodesToLabels, mapClaimStatusLabelToCodes } from './claimStatusRequests';
import { parseQueryFilterParams } from 'components/forms/schema/claimsAdvancedFilterSchema';
import { OPERATORS } from 'lib/SearchQueryBuilder/config/queryConfig';
import { prepareQueryGroup, prepareQueryRule } from 'utils/queryBuilder';
import { CriteriaKey, filterDataKeys } from 'utils/adfUtils';
import { buildCriteria, buildQueryFromCriteria } from 'utils/advancedFilterUtils';
import { MONTH_YEAR_FORMAT } from '@veritable/common/constants';
import { WORKLIST_PRIORITIES, WORKLIST_SEARCH_CRITERIA, WORKLIST_TYPES } from 'app-constants/claims/workLists';

const DEFAULT_CONJUNCTION = 'OR';

const getMonthYearFormat = (date?: string): string | null => {
	if (!date) {
		return null;
	}
	return dateParser(date).format(MONTH_YEAR_FORMAT);
};

const formKeys: Record<keyof ClaimWorkLists.IFormModel, keyof ClaimWorkLists.IFormModel> = {
	name: 'name',
	description: 'description',
	priority: 'priority',
	assignees: 'assignees',
	criteria: 'criteria',
	tableMeta: 'tableMeta',
	totalCount: 'totalCount',
};

const formVsApiModelKeysMapping: Partial<Record<keyof ClaimWorkLists.IFormModel, keyof ClaimWorkLists.IWorkListPostModel>> = {
	name: 'name',
	description: 'description',
	priority: 'priority',
	assignees: 'assignees',
	criteria: 'criteria',
};

const prepareWorkListFormInitialModel = ({
	formType,
	model,
	worklistType,
}: {
	formType: ClaimWorkLists.IFormType;
	model?: ClaimWorkLists.IWorkListResponse;
	worklistType: ClaimWorkLists.IWorkListType;
}): ClaimWorkLists.IFormModel => {
	const { edit: isEdit, duplicate: isDuplicate } = formType;
	const shouldAutoFill = isDuplicate || isEdit;
	const isAuto = worklistType === WORKLIST_TYPES.auto.key;
	const shouldAutoFillCriteria = isAuto && shouldAutoFill;

	const adfCriteria = buildQueryFromCriteria(model?.criteria?.filters?.[0] as Record<CriteriaKey, JsonGroup>);

	const criteria = shouldAutoFillCriteria
		? parseQueryFilterParams({
				query: adfCriteria,
				field: filterDataKeys.claimStatus,
				parser: mapClaimStatusCodesToLabels,
			}) || null
		: null;

	const editFields = {
		criteria,
		tableMeta: { ...defaultTableSelectionMeta, criteria },
		totalCount: 0,
	};

	if (isEdit) {
		return editFields as unknown as ClaimWorkLists.IFormModel;
	}
	return {
		name: isDuplicate ? `Copy of ${model?.name}` : model?.name || '',
		description: shouldAutoFill ? model?.description || '' : '',
		priority: shouldAutoFill ? model?.priority || WORKLIST_PRIORITIES.medium.value : WORKLIST_PRIORITIES.medium.value,
		assignees: shouldAutoFill ? model?.assignees || [] : [],
		...editFields,
	};
};

// submit model criteria preparation for claim status filter
const prepareSubmitModelCriteria = (criteria: JsonTree | null): JsonTree | null => {
	if (_.isEmpty(criteria)) {
		return null;
	}
	return parseQueryFilterParams({
		query: criteria,
		field: filterDataKeys.claimStatus,
		parser: mapClaimStatusLabelToCodes,
	});
};

// get claim id criteria operator and value for manual worklist
const getClaimIdCriteriaOperatorAndValue = (
	tableMeta: ITableSelectionMeta,
): { operator: string; value: string[] | string[][] | null; valueType: string[] } => {
	const hasSelectedRows = !_.isEmpty(tableMeta?.selectedRows);
	const hasDeselectedRows = !_.isEmpty(tableMeta?.deselectedRows);

	if (hasSelectedRows) {
		return {
			operator: OPERATORS.multiselectContains,
			value: [tableMeta?.selectedRows],
			valueType: ['multiselect'],
		};
	} else if (hasDeselectedRows) {
		return {
			operator: OPERATORS.multiselectNotContains,
			value: [tableMeta?.deselectedRows],
			valueType: ['multiselect'],
		};
	} else {
		return {
			operator: OPERATORS.isNotNull,
			value: [],
			valueType: ['text'],
		};
	}
};

// process all criteria groups for manual worklists
const processAllCriteriaGroups = (tableSelectionMeta: ITableSelectionMeta): JsonTree | null => {
	const clonedItem = _.cloneDeep(tableSelectionMeta);
	const mappedCriteria = (() => {
		if (clonedItem?.selectedRows?.length === 0 && !clonedItem?.selectAll) {
			return null;
		}

		let newRules = [];
		const allRules = clonedItem?.criteria?.children1?.[0]?.children1;
		const hasSelectedRows = !_.isEmpty(clonedItem?.selectedRows);
		const hasDeselectedRows = !_.isEmpty(clonedItem?.deselectedRows);

		const claimIdRule = prepareQueryRule({
			field: filterDataKeys.claimId,
			...getClaimIdCriteriaOperatorAndValue(clonedItem),
		});

		if (clonedItem?.selectAll && !hasDeselectedRows) {
			newRules = allRules;
		} else if (hasSelectedRows) {
			newRules = [claimIdRule];
		} else {
			newRules = [...allRules, claimIdRule];
		}
		clonedItem.criteria.children1[0].children1 = newRules;
		return clonedItem.criteria;
	})();
	if (_.isEmpty(mappedCriteria)) {
		return null;
	}
	return mappedCriteria as JsonTree;
};

const preparePostModel = (
	model: ClaimWorkLists.IFormModel,
	worklistType: ClaimWorkLists.IWorkListType,
	claimAdfFieldsMap: Partial<Record<keyof ClaimStatusRequests.IClaimSearchCriteria, string[]>>,
): ClaimWorkLists.IWorkListPostModel => {
	const submitModel: ClaimWorkLists.IWorkListPostModel = {
		type: worklistType,
		name: model?.name,
		priority: model?.priority,
	};
	if (model?.description) {
		submitModel.description = model.description;
	}
	if (!_.isEmpty(model?.assignees)) {
		submitModel.assignees = model.assignees;
	}

	if (worklistType === WORKLIST_TYPES.auto.key && !_.isEmpty(model?.criteria)) {
		const criteriaTree = prepareSubmitModelCriteria(model?.criteria as JsonTree);
		submitModel.criteria = {
			conjunction: DEFAULT_CONJUNCTION,
			filters: [buildCriteria(criteriaTree, claimAdfFieldsMap)],
		};
	} else if (worklistType === WORKLIST_TYPES.manual.key) {
		const processedCriteria = processAllCriteriaGroups(model?.tableMeta);
		if (_.isEmpty(processedCriteria)) {
			submitModel.criteria = null;
		} else {
			const selectionCriteria = buildCriteria(_.cloneDeep(processedCriteria), claimAdfFieldsMap);
			submitModel.criteria = {
				conjunction: DEFAULT_CONJUNCTION,
				filters: [selectionCriteria],
			};
		}
	}

	return submitModel;
};

const preparePatchModel = (
	initialModel: ClaimWorkLists.IFormModel,
	formModel: ClaimWorkLists.IFormModel,
	worklistType: ClaimWorkLists.IWorkListType,
	claimAdfFieldMap: Partial<Record<keyof ClaimStatusRequests.IClaimSearchCriteria, string[]>>,
): ClaimWorkLists.IWorkListUpdateModel => {
	const keysChanged = _.difference(
		_.keys(formModel),
		_.keys(_.pickBy(formModel, (value, key) => _.isEqual(value, _.get(initialModel, key)))),
	);

	// Use formVsApiModelKeysMapping to map form keys to API model keys
	const patchModel: Partial<ClaimWorkLists.IWorkListUpdateModel> = { type: worklistType };

	// Only include changed fields that have mappings
	_.forEach(keysChanged, (key) => {
		const apiKey = formVsApiModelKeysMapping?.[key];
		if (apiKey) {
			patchModel[apiKey] = _.get(formModel, key);
		}
		if (key === formKeys.criteria && worklistType === WORKLIST_TYPES.auto.key) {
			if (_.isEmpty(formModel?.criteria)) {
				patchModel.criteria = null;
			} else {
				const criteriaTree = prepareSubmitModelCriteria(formModel?.criteria as JsonTree);
				patchModel.criteria = {
					conjunction: DEFAULT_CONJUNCTION,
					filters: [buildCriteria(criteriaTree, claimAdfFieldMap)],
				};
			}
		}
	});

	// Handle manual worklist type with claim criteria
	if (worklistType === WORKLIST_TYPES.manual.key) {
		const processedCriteria = processAllCriteriaGroups(formModel?.tableMeta);
		if (_.isEmpty(processedCriteria)) {
			patchModel.criteria = null;
		} else {
			const selectionCriteria = buildCriteria(_.cloneDeep(processedCriteria), claimAdfFieldMap);
			patchModel.criteria = {
				conjunction: DEFAULT_CONJUNCTION,
				filters: [selectionCriteria],
			};
		}
	}
	return patchModel;
};

const ADD_FORM_TYPE: ClaimWorkLists.IFormType = { add: true };
const DUPLICATE_FORM_TYPE: ClaimWorkLists.IFormType = { duplicate: true };
const EDIT_FORM_TYPE: ClaimWorkLists.IFormType = { edit: true };

const getManualWorklistFilterQuery = (): JsonTree => {
	const manualWorklistRule = prepareQueryRule({
		field: WORKLIST_SEARCH_CRITERIA.type.key,
		operator: OPERATORS.equal,
		value: [WORKLIST_TYPES.manual.key],
		valueType: ['select'],
	});
	return prepareQueryGroup({ children: [manualWorklistRule] });
};

// prepare claim id criteria with query builder params for manual worklist
const prepareClaimIdCriteria = (tableMeta: ITableSelectionMeta): JsonTree => {
	const claimIdRule = prepareQueryRule({
		field: filterDataKeys.claimId,
		valueSrc: ['value'],
		...getClaimIdCriteriaOperatorAndValue(tableMeta),
	});
	return prepareQueryGroup({ children: [claimIdRule] });
};

// get total selection count for manual worklist from table selection meta
const getTotalSelectionCount = (tableMeta: ITableSelectionMeta, totalCount: number): number => {
	if (tableMeta?.selectAll) {
		return totalCount - tableMeta?.deselectedRows?.length;
	}
	return tableMeta?.selectCount || 0;
};

// prepare manual worklist modification criteria with query builder params (add or remove from manual worklist)
const prepareManualWorklistModificationCriteria = (tableMeta: ITableSelectionMeta): JsonTree => {
	if (_.isEmpty(tableMeta?.criteria)) {
		return prepareClaimIdCriteria(tableMeta);
	} else {
		return processAllCriteriaGroups(tableMeta);
	}
};

// check if worklist is owned by user
const isWorklistAssignedToUser = (claimWorkList: ClaimWorkLists.IWorkListResponse, user: App.User): boolean => {
	return Boolean(user?.id && _.includes(claimWorkList?.assignees ?? [], user.id));
};

export {
	formKeys,
	ADD_FORM_TYPE,
	EDIT_FORM_TYPE,
	DUPLICATE_FORM_TYPE,
	preparePostModel,
	preparePatchModel,
	getMonthYearFormat,
	getTotalSelectionCount,
	prepareClaimIdCriteria,
	processAllCriteriaGroups,
	getManualWorklistFilterQuery,
	prepareWorkListFormInitialModel,
	prepareManualWorklistModificationCriteria,
	isWorklistAssignedToUser,
};
